import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { parseScenario, serialiseScenario, type Scenario } from './scenario'

export interface ScenarioHandle {
  scenario: Scenario
  /** Tokens a damaged link contained, for a banner rather than a crash. */
  warnings: string[]
  /** The canonical query string, which is also the cache key. */
  query: string
  /**
   * Write a new scenario to the URL.
   *
   * Pushes by default, so the back button walks through scenarios -- which is
   * the behaviour asked for. Continuous controls pass `replace` instead: a
   * dragged slider would otherwise bury the previous scenario under twenty
   * history entries.
   */
  set: (
    next: Scenario | ((current: Scenario) => Scenario),
    options?: { replace?: boolean },
  ) => void
  reset: () => void
}

/**
 * The URL is the only state. This is the whole of the state layer.
 *
 * `/config` uses it, so a link to it reproduces a scenario exactly: there is
 * nothing else to carry over.
 */
export function useScenario(): ScenarioHandle {
  const [params, setParams] = useSearchParams()

  const { scenario, warnings } = useMemo(() => parseScenario(params), [params])
  const query = useMemo(() => serialiseScenario(scenario), [scenario])

  const set = useCallback<ScenarioHandle['set']>(
    (next, options) => {
      const resolved = typeof next === 'function' ? next(scenario) : next
      setParams(new URLSearchParams(serialiseScenario(resolved)), {
        replace: options?.replace ?? false,
      })
    },
    [scenario, setParams],
  )

  const reset = useCallback(() => setParams(new URLSearchParams()), [setParams])

  return { scenario, warnings, query, set, reset }
}
